<template>
  <div class="flex flex-col gap-y-4">
    <div class="soar-container flex flex-col pt-12">
      <div class="w-full flex justify-center">
        <HeaderTypo class="text-center flex gap-x-2">
          <span>{{ $t('quickSearch') }}</span>
          -
          <span>{{ $t('serviceCard') }}</span>
        </HeaderTypo>
      </div>
      <div class="md:px-28 lg:px-60 py-4 w-full flex justify-center">
        <div
          class="p-0.5 w-full relative flex justify-center items-center rounded border border-brand-gray-300 bg-emerald-50"
        >
          <input
            :placeholder="$t('startwriteServiceName')"
            type="text"
            class="px-3 py-2 w-full outline-none"
            v-model="searchValue"
          />
          <div class="absolute end-0.5 top-0.5 w-10 h-10 flex justify-center items-center">
            <faMagnifyingGlass class="w-4 fill-brand-black-body" />
          </div>
        </div>
      </div>
    </div>

    <div class="flex-1 h-full flex flex-col gap-y-3">
      <div
        class="p-4 w-full h-[60vh] md:h-[65vh] grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 overflow-y-auto"
      >
        <div
          :key="key"
          v-for="(item, key) in pageContent"
          class="p-4 max-h-[18rem] rounded bg-white border border-primary shadow-md flex flex-col items-center gap-y-3"
        >
          <small
            class="w-full h-9 md:h-12 pb-1 text-[10px] md:text-xs text-center border-b border-brand-gray-600"
          >
            {{ item.serviceDescription }}
          </small>
          <faIdCard class="w-8 fill-brand-black-secondary" />
          <p class="flex-1 text-xs md:text-sm text-center">
            {{ item.localizedServiceName || item.serviceFullDescription }}
          </p>

          <FilledButton
            class="text-white py-1 px-4 text-sm md:text-base"
            @click="onServiceClick(item)"
          >
            {{ $t('serviceDetails') }}
          </FilledButton>
        </div>
      </div>
      <div class="p-4 w-full soar-container flex justify-center">
        <Paginate
          :page-count="paginatorConfig.pageCount"
          :page-range="paginatorConfig.itemsPerPage"
          :click-handler="onPageClick"
          :prev-text="'‹'"
          :next-text="'›'"
          :first-last-button="true"
          container-class="pagination"
          first-button-text="«"
          last-button-text="»"
        >
        </Paginate>
      </div>
    </div>
  </div>
</template>

<script setup>
import faIdCard from '@/assets/icons/faIdCard.svg?component'
import faMagnifyingGlass from '@/assets/icons/faMagnifyingGlass.svg?component'
import FilledButton from '@/components/Buttons/Filled.vue'
import quickSearchServices from '@/services/quickSearch'
import { useGlobalStore } from '@/stores/global'
import Fuse from 'fuse.js'
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import Paginate from 'vuejs-paginate-next'

const globalStore = useGlobalStore()
const router = useRouter()

const paginatorConfig = reactive({
  totalItemCount: 0,
  itemsPerPage: 5,
  currentPage: 1,
  pageCount: 0
})

const pageLength = 8
const isLoading = ref(true)
const modules = ref()
const nameDetails = ref()
const searchValue = ref()
const fuse = ref()

const allServices = computed(() => {
  if (isLoading.value) return

  const services = []
  modules.value.forEach((module) => {
    module.moduleServices.forEach((parentService) => {
      parentService.childs.forEach((subService) => {
        subService.serviceTransactions.forEach((transaction) => {
          const fullserviceDescription = nameDetails.value.find(
            (nd) => +nd.serviceTransId === +transaction.id
          )

          const serviceName = fullserviceDescription.childTransactionDescription

          services.push({
            localizedServiceName: serviceName,
            moduleId: module.id,
            transactionId: transaction.id,
            beneficiaryApplicationIds: transaction.beneficiaryApplicationIds,
            serviceDescription: fullserviceDescription.serviceDescription,
            serviceFullDescription: fullserviceDescription.serviceFullDescription
          })
        })
      })
    })
  })

  return services
})

const filteredContent = computed(() => {
  if (!allServices.value || !searchValue.value) return allServices.value
  const results = fuse.value.search(searchValue.value).map((fc) => fc.item)
  return results
})

const pageContent = computed(() => {
  if (!allServices.value) return []

  const startIndex = (paginatorConfig.currentPage - 1) * pageLength
  const endIndex = Math.min(startIndex + pageLength, allServices.value.length)

  return filteredContent.value.slice(startIndex, endIndex)
})

onMounted(async () => {
  modules.value = await quickSearchServices.fetchTransactions()
  nameDetails.value = await quickSearchServices.fetchNameDetails()
  isLoading.value = false

  paginatorConfig.totalItemCount = filteredContent.value?.length ?? 0
  paginatorConfig.pageCount = Math.ceil((filteredContent.value?.length || 1) / pageLength)

  fuse.value = new Fuse(allServices.value, {
    includeScore: false,
    threshold: 0.3,
    keys: ['localizedServiceName', 'serviceDescription', 'serviceFullDescription']
  })
})

const onPageClick = (pageNum) => {
  paginatorConfig.currentPage = pageNum
}

const onServiceClick = async (transaction) => {
  const data = await quickSearchServices.fetchConfig(transaction.transactionId)
  globalStore.$patch({ topbarState: null })
  router.push({
    name: 'ServiceCardView',
    params: {
      serviceId: transaction.transactionId
    },
    query: {
      applicationId: data.applicationId
    }
  })
}

watch(searchValue, () => {
  paginatorConfig.totalItemCount = filteredContent.value?.length ?? 0
  paginatorConfig.pageCount = Math.ceil((filteredContent.value?.length || 1) / pageLength)
})
</script>

<script>
export default { name: 'QuickSearch' }
</script>
